import { useCallback, useEffect, useRef, useState } from 'react';
import { analyzeVerificationFrame, releaseVerificationResources } from './nativeVibeVerification';
import { FRAME_SAMPLE_INTERVAL_MS } from './poseVerificationConfig';
import type { VibeAnalysisResult } from './types';

type PermissionState = 'pending' | 'granted' | 'denied';

/**
 * Web counterpart to useVibeCameraSession.ts — owns a getUserMedia()
 * MediaStream instead of a VisionCamera device/session. Same external
 * contract shape as native isn't required here (the two platforms use
 * entirely separate modal components — see VibeVerificationCameraModal.web.tsx),
 * but the same lifecycle rules apply: `sessionActive` gates the whole camera
 * stream (stopped fully on false), `pollingActive` gates only the
 * FRAME_SAMPLE_INTERVAL_MS analysis loop, and a shared busy-flag mutex
 * prevents the analysis loop and the final evidence-photo capture from
 * racing each other on the same offscreen canvas.
 */
export function useVibeCameraSession(
  sessionActive: boolean,
  pollingActive: boolean,
  onAnalysis: (result: VibeAnalysisResult) => void,
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState>('pending');
  const [streamReady, setStreamReady] = useState(false);
  const captureBusyRef = useRef(false);
  const onAnalysisRef = useRef(onAnalysis);
  onAnalysisRef.current = onAnalysis;

  useEffect(() => {
    if (!sessionActive) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        setPermissionState('granted');
        setStreamReady(true);
      } catch {
        if (!cancelled) setPermissionState('denied');
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStreamReady(false);
    };
  }, [sessionActive]);

  // Deliberately a separate effect from the getUserMedia() one above, not a
  // direct `videoRef.current.srcObject = stream` assignment inside it: the
  // <video> element only mounts once `permissionState==='granted'` (see
  // VibeVerificationCameraModal.web.tsx's conditional render), which is a
  // state update this hook itself triggers — so at the moment the
  // getUserMedia() promise resolves, the ref is still null (the video tag
  // hasn't rendered yet on this pass). Reproduced live: without this,
  // `videoRef.current` stayed null forever, and the preview was a
  // permanently black frame (readyState 0) despite permission having been
  // granted and the stream existing in `streamRef`. This effect instead runs
  // AFTER the render/commit that mounts the video element (React runs
  // effects post-commit), by which point the ref is guaranteed populated.
  useEffect(() => {
    if (!streamReady || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => undefined);
  }, [streamReady]);

  // Object-fit: cover crop math — same approach as src/web/cropCapture.web.ts
  // — so the analysis frame and the final evidence photo both match native's
  // portrait framing regardless of the camera's actual aspect ratio.
  const captureFrameDataUrl = useCallback((quality: number, targetWidth: number, targetHeight: number): string | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return null;
    if (!analysisCanvasRef.current) analysisCanvasRef.current = document.createElement('canvas');
    const canvas = analysisCanvasRef.current;
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const scale = Math.max(targetWidth / video.videoWidth, targetHeight / video.videoHeight);
    const sourceWidth = targetWidth / scale;
    const sourceHeight = targetHeight / scale;
    const sourceX = (video.videoWidth - sourceWidth) / 2;
    const sourceY = (video.videoHeight - sourceHeight) / 2;
    ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);
    return canvas.toDataURL('image/jpeg', quality);
  }, []);

  const captureAndAnalyzeTick = useCallback(async () => {
    if (captureBusyRef.current) return;
    captureBusyRef.current = true;
    try {
      // Lower-res/quality than the final evidence photo — this is sampled
      // every 400ms purely to drive pose gating, not saved anywhere.
      const frame = captureFrameDataUrl(0.6, 360, 480);
      if (!frame) return;
      const result = await analyzeVerificationFrame(frame);
      if (result) onAnalysisRef.current(result);
    } catch (err) {
      if (__DEV__) console.log('[VibePoseDebug][Web] polling tick failed', err);
    } finally {
      captureBusyRef.current = false;
    }
  }, [captureFrameDataUrl]);

  useEffect(() => {
    if (!pollingActive || !streamReady) return undefined;
    const intervalId = setInterval(() => {
      captureAndAnalyzeTick();
    }, FRAME_SAMPLE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [pollingActive, streamReady, captureAndAnalyzeTick]);

  useEffect(() => () => {
    releaseVerificationResources().catch(() => undefined);
  }, []);

  const captureFinalPhoto = useCallback(async (): Promise<string> => {
    const waitStartedAt = Date.now();
    while (captureBusyRef.current) {
      if (Date.now() - waitStartedAt > 3000) throw new Error('capture_busy_timeout');
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
    }
    captureBusyRef.current = true;
    try {
      // Matches native's targetResolution (720x960) exactly.
      const dataUrl = captureFrameDataUrl(0.8, 720, 960);
      if (!dataUrl) throw new Error('capture_failed_no_frame');
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } finally {
      captureBusyRef.current = false;
    }
  }, [captureFrameDataUrl]);

  return {
    videoRef,
    permissionState,
    captureFinalPhoto,
  };
}
